/* eslint-disable no-console */
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useContext, useState, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';

import { RatingIcon } from '../../Icons';
import { ToolsListItem } from './ToolsListItem';
import { Card } from '../Card';

import { hex2rgba } from '../../helpers/shared';
import { UserContext } from '../../context/userContext';
import { TrialModal } from '../TrialModal';
import { PaywallModal } from '../PaywallModal';
import { IBrushFields } from '../../@types/generated/contentful';

type Props = {
  set: IBrushFields;
  isOpen: boolean;
  isBrushset?: boolean;
  onClose: () => void;
};

const ToolsModal = ({ set, isOpen, isBrushset, onClose }: Props) => {
  const [isTrialOpen, setTrialOpen] = useState(false);
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [rating, setRating] = useState<null | string>();

  const { image, color, section: brushes, title, description, fontColor } = set;

  const { user } = useContext(UserContext);
  const { isNewSubscriber = true, hasPremium = false, plan = null } = user || {};

  const handleDownloadButtonClick = () => {
    if (user && user.email && !isNewSubscriber) {
      setPaywallOpen(true);
      mixpanel.track('Paywall', { premium: false, trial: false });
    } else {
      setTrialOpen(true);
      mixpanel.track('Paywall trial', { premium: false, trial: false });
    }
  };

  const handleRatingClick = (value: number) => {
    if (rating) {
      return;
    }
    localStorage.setItem(`rating_${title}`, value.toString());
    setRating(value.toString());
    mixpanel.track('Ratting tapped', { set: title, value });
  };

  useEffect(() => {
    const currentRating = localStorage.getItem(`rating_${title}`);
    setRating(currentRating);
  }, [setRating, title]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
          relative 
        inline-block 
        overflow-hidden 
        transform 
        transition-all s
        align-middle 
        w-[683px]
        md:w-full
        `}
            >
              <Card theme='light' size='large' onClose={onClose}>
                <div>
                  <div
                    className={`h-[380px] 
                relative 
                overflow-hidden
                rounded-t-[22px] md:rounded-none flex items-center justify-center`}
                    style={{ backgroundColor: color }}
                  >
                    <img
                      src={`http:${image?.fields.file.url}`}
                      alt='brush'
                      loading='lazy'
                      className='max-w-[100%] max-h-[80%] select-none'
                    />
                    <div
                      className='p-6 md:p-4 absolute flex bottom-0 justify-between w-full'
                      style={{
                        backgroundImage: `linear-gradient(to top, ${color} 0%, ${hex2rgba(
                          color || '#ffffff',
                          0
                        )} 100%)`,
                      }}
                    >
                      <div className='text-left max-h-full'>
                        <h1 className='text-3xl leading-3xl font-bold' style={{ color: fontColor }}>
                          {title}
                        </h1>
                        {isBrushset && brushes && (
                          <p className='text-lg leading-lg font-medium text-white' style={{ color: fontColor }}>
                            {brushes.length} {brushes.length > 1 ? 'brushes' : 'brush'}
                          </p>
                        )}
                        <p
                          className='text-lg leading-lg xs:text-sm xs-text-sm'
                          style={{ color: hex2rgba(fontColor || '#000000', 0.7) }}
                        >
                          {description}
                        </p>
                      </div>
                      <div className='max-h-min whitespace-nowrap self-end'>
                        <div style={{ color: hex2rgba(fontColor || '#000000', 0.5) }}>
                          <RatingIcon
                            className={`w-[27px] ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(1)}
                            style={{ color: Number(rating) >= 1 ? fontColor : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(2)}
                            style={{ color: Number(rating) >= 2 ? fontColor : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(3)}
                            style={{ color: Number(rating) >= 3 ? fontColor : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(4)}
                            style={{ color: Number(rating) >= 4 ? fontColor : '' }}
                          />
                          <RatingIcon
                            className={`w-[27px] ml-1 ${rating ? '' : 'cursor-pointer'} xs:w-[20px] inline-block`}
                            onClick={() => handleRatingClick(5)}
                            style={{ color: Number(rating) >= 5 ? fontColor : '' }}
                          />
                        </div>
                        <p
                          className='text-lg leading-lg text-white font-medium text-right'
                          style={{ color: fontColor }}
                        >
                          {rating ? 'Thank you!' : 'Tap to rate'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {brushes && (
                    <ul className='pt-4 pb-8'>
                      {brushes.map((item, i: number) => (
                        <li key={item.fields.title}>
                          <ToolsListItem
                            setTitle={title}
                            brush={item}
                            isLastItem={i === brushes.length - 1}
                            image={image}
                            onDownload={handleDownloadButtonClick}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
        <TrialModal
          isOpen={isTrialOpen}
          onClose={() => setTrialOpen(false)}
          userId={(user && user.id) || null}
          email={(user && user.email) || null}
        />
        <PaywallModal
          isOpen={isPaywallOpen}
          onClose={() => setPaywallOpen(false)}
          userId={(user && user.id) || null}
          email={(user && user.email) || null}
          hasPremium={hasPremium}
          isTrial={(plan && plan.isTrial) || false}
        />
      </Dialog>
    </Transition.Root>
  );
};

export { ToolsModal };
